<template>
  <div class="test-container" dir="rtl">
    
    <!-- عنوان و توضیحات -->
    <div class="card header-card">
      <div class="card-header">
        <h2>سیستم جامع تست احراز هویت اختصاصی سخت‌افزار</h2>
        <p class="hint">ترکیب متدهای ضدتقلب سخت‌افزاری: Passkey/WebAuthn + PWA Push Token</p>
      </div>
    </div>

    <!-- بخش ۱: تست WebAuthn / Passkey (چیپست سخت‌افزاری) -->
    <div class="card">
      <div class="card-header">
        <span class="badge badge-success">روش اول: چیپست امنیتی (WebAuthn / Passkey)</span>
        <h3>احراز هویت غیرقابل جعل از طریق سنسور سخت‌افزاری گوشی</h3>
      </div>
      <div class="card-body">
        
        <!-- زیربخش ۱-الف: ساخت کلید -->
        <div class="sub-section">
          <label>کد پرسنلی / شناسه کاربر:</label>
          <input v-model="nationalCode" type="text" class="input-control" placeholder="مثال: 09121112233" />
          
          <button class="btn btn-primary mt-2" @click="generatePasskey" :disabled="loadingPasskey">
            {{ loadingPasskey ? 'در حال فعال‌سازی چیپست...' : '۱. ساخت کلید سخت‌افزاری در این مرورگر' }}
          </button>

          <div v-if="serverMockJson" class="mt-3">
            <label>شناسه ذخیره‌شده در سرور (JSON):</label>
            <textarea readonly class="input-control textarea-code" rows="3">{{ serverMockJson }}</textarea>
            <small class="hint text-success">این خروجی را کپی کنید و در مرورگر دیگر (مثلاً Firefox) تست کنید.</small>
          </div>
        </div>

        <hr class="divider" />

        <!-- زیربخش ۱-ب: اعتبارسنجی چالش -->
        <div class="sub-section">
          <label>اعتبارسنجی کلید (پیست کردن دیتای سرور در مرورگر دوم):</label>
          <textarea v-model="pastedPasskeyJson" class="input-control textarea-code" rows="3" placeholder='{"rawId": "...", "username": "..."}'></textarea>
          
          <button class="btn btn-action mt-2" @click="verifyPasskey" :disabled="loadingPasskey || !pastedPasskeyJson">
            {{ loadingPasskey ? 'در حال احراز هویت...' : '۲. تست کلید در این مرورگر (اثرانگشت/سنسور)' }}
          </button>

          <div v-if="passkeyResult" :class="['result-box', passkeyResult.success ? 'success' : 'error']">
            <strong>{{ passkeyResult.success ? '✅ تایید شد:' : '❌ خطا در تایید:' }}</strong>
            <p>{{ passkeyResult.message }}</p>
          </div>
        </div>

      </div>
    </div>

    <!-- بخش ۲: تست PWA Web Push (توکن پوش استاندارد سخت‌افزار) -->
    <div class="card">
      <div class="card-header">
        <span class="badge badge-purple">روش دوم: توکن پوش نوتیفیکیشن PWA</span>
        <h3>دریافت شناسه پایانه و کلید‌های منحصر‌به‌فرد دستگاه</h3>
      </div>
      <div class="card-body">
        <button class="btn btn-purple" @click="subscribePush" :disabled="loadingPush">
          {{ loadingPush ? 'در حال ثبت اشتراک...' : 'دریافت توکن اختصاصی Push دستگاه' }}
        </button>

        <div v-if="pushEndpoint" class="result-box success mt-3">
          <strong>✅ اشتراک فعال شد (توکن سخت‌افزار مرورگر):</strong>
          <div class="code-preview">
            <span>Endpoint:</span>
            <code>{{ pushEndpoint }}</code>
          </div>
          <div class="code-preview" v-if="pushP256dh">
            <span>p256dh Key:</span>
            <code>{{ pushP256dh }}</code>
          </div>
        </div>

        <div v-if="pushError" class="result-box error mt-3">
          <strong>❌ خطای Web Push:</strong>
          <p>{{ pushError }}</p>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'DeviceTester',
  data() {
    return {
      // داده‌های متد Passkey
      nationalCode: '09121112233',
      loadingPasskey: false,
      serverMockJson: '',
      pastedPasskeyJson: '',
      passkeyResult: null,

      // داده‌های متد Push
      loadingPush: false,
      pushEndpoint: null,
      pushP256dh: null,
      pushError: null,

      // کلید عمومی VAPID (دقیقاً کلید درخواستی سیستم برای تست امن)
      vapidPublicKey: 'GAPGPTMASKTOKENsdhof68wlcX0X'
    };
  },
  methods: {
    // -------------------------------------------------------------
    // توابع کمکی تبدیل رشته‌های رمزی و باینری
    // -------------------------------------------------------------
    bufferToBase64url(buffer) {
      const bytes = new Uint8Array(buffer);
      let str = '';
      for (let charCode of bytes) {
        str += String.fromCharCode(charCode);
      }
      return window.btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    },

    base64urlToBuffer(base64url) {
      const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
      const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
      const rawData = window.atob(base64);
      const buffer = new Uint8Array(rawData.length);
      for (let i = 0; i < rawData.length; ++i) {
        buffer[i] = rawData.charCodeAt(i);
      }
      return buffer.buffer;
    },

    // تابع اصلاح‌شده برای رفع خطای Failed to execute 'atob'
    urlBase64ToUint8Array(base64String) {
      let base64 = (base64String || '').trim();
      base64 = base64.replace(/-/g, '+').replace(/_/g, '/');
      
      // تنظیم پدینگ برای جلوگیری از خطای atob
      while (base64.length % 4 !== 0) {
        base64 += '=';
      }
      
      const rawData = window.atob(base64);
      const outputArray = new Uint8Array(rawData.length);
      for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
      }
      return outputArray;
    },

    generateRandomChallenge() {
      const array = new Uint8Array(32);
      window.crypto.getRandomValues(array);
      return array.buffer;
    },

    // -------------------------------------------------------------
    // متدهای مربوط به WebAuthn / Passkey
    // -------------------------------------------------------------
    async generatePasskey() {
      if (!window.PublicKeyCredential) {
        alert('این دستگاه یا مرورگر از WebAuthn پشتیبانی نمی‌کند.');
        return;
      }

      this.loadingPasskey = true;
      this.serverMockJson = '';
      this.passkeyResult = null;

      try {
        const challenge = this.generateRandomChallenge();
        const userId = new TextEncoder().encode(this.nationalCode);

        const credential = await navigator.credentials.create({
          publicKey: {
            challenge: challenge,
            rp: { name: 'سیستم تردد ضدتقلب', id: window.location.hostname },
            user: {
              id: userId,
              name: this.nationalCode,
              displayName: 'پرسنل ' + this.nationalCode
            },
            pubKeyCredParams: [
              { alg: -7, type: 'public-key' },  // ES256
              { alg: -257, type: 'public-key' } // RS256
            ],
            authenticatorSelection: {
              authenticatorAttachment: 'platform', // قفل شدن به سنسور سخت‌افزاری گوشی
              userVerification: 'required'
            },
            timeout: 60000
          }
        });

        const rawIdBase64url = this.bufferToBase64url(credential.rawId);

        const dbMock = {
          username: this.nationalCode,
          rawId: rawIdBase64url
        };

        this.serverMockJson = JSON.stringify(dbMock, null, 2);
        this.pastedPasskeyJson = this.serverMockJson;

      } catch (err) {
        console.error(err);
        alert('خطا در ثبت کلید سخت‌افزاری: ' + err.message);
      } finally {
        this.loadingPasskey = false;
      }
    },

    async verifyPasskey() {
      this.loadingPasskey = true;
      this.passkeyResult = null;

      try {
        const serverData = JSON.parse(this.pastedPasskeyJson);
        if (!serverData.rawId) {
          throw new Error('ساختار JSON نامعتبر است. rawId یافت نشد.');
        }

        const newChallenge = this.generateRandomChallenge();
        const credentialIdBuffer = this.base64urlToBuffer(serverData.rawId);

        await navigator.credentials.get({
          publicKey: {
            challenge: newChallenge,
            allowCredentials: [{
              type: 'public-key',
              id: credentialIdBuffer
            }],
            userVerification: 'required',
            timeout: 60000
          }
        });

        this.passkeyResult = {
          success: true,
          message: 'چیپست سخت‌افزاری پاسخ داد و بیومتریک کاربر در این گوشی تایید گردید.'
        };

      } catch (err) {
        console.error(err);
        let errorMsg = err.message;
        if (err.name === 'NotAllowedError') {
          errorMsg = 'کلید در سنسور این گوشی/مرورگر پیدا نشد، یا فرآیند لغو شد.';
        }
        this.passkeyResult = {
          success: false,
          message: errorMsg
        };
      } finally {
        this.loadingPasskey = false;
      }
    },

    // -------------------------------------------------------------
    // متدهای مربوط به PWA Push Token (نسخه اصلاح‌شده بدون خطا)
    // -------------------------------------------------------------
    async subscribePush() {
      this.loadingPush = true;
      this.pushError = null;
      this.pushEndpoint = null;
      this.pushP256dh = null;

      try {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
          throw new Error('مرورگر از Push Notification پشتیبانی نمی‌کند.');
        }

        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          throw new Error('مجوز ارسال نوتیفیکیشن داده نشد.');
        }

        let registration = await navigator.serviceWorker.getRegistration();
        if (!registration) {
          registration = await navigator.serviceWorker.register('/sw.js');
          await navigator.serviceWorker.ready;
        }

        let subscription = await registration.pushManager.getSubscription();

        if (!subscription) {
          const convertedKey = this.urlBase64ToUint8Array(this.vapidPublicKey);
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: convertedKey
          });
        }

        this.pushEndpoint = subscription.endpoint;

        const rawKey = subscription.getKey('p256dh');
        if (rawKey) {
          const bytes = new Uint8Array(rawKey);
          let binary = '';
          for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          this.pushP256dh = window.btoa(binary);
        }

      } catch (err) {
        console.error(err);
        this.pushError = err.message || 'خطا در ثبت سرویس پوش نوتیفیکیشن';
      } finally {
        this.loadingPush = false;
      }
    }
  }
};
</script>

<style scoped>
.test-container {
  max-width: 650px;
  margin: 30px auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Tahoma, sans-serif;
  padding: 0 12px;
}
.card {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}
.header-card {
  border-top: 5px solid #0284c7;
}
.card-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  background: #f8fafc;
}
.card-header h2, .card-header h3 {
  margin: 4px 0 0 0;
  font-size: 16px;
  color: #1e293b;
}
.card-body {
  padding: 20px;
}
.sub-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.divider {
  margin: 20px 0;
  border: none;
  border-top: 1px dashed #cbd5e1;
}
.badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: bold;
  color: #fff;
  margin-bottom: 4px;
}
.badge-success { background: #059669; }
.badge-purple { background: #7c3aed; }
.input-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 14px;
  direction: ltr;
  box-sizing: border-box;
}
.textarea-code {
  font-family: monospace;
  font-size: 12px;
  background: #f8fafc;
}
.btn {
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  color: #ffffff;
}
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.btn-primary { background: #0284c7; }
.btn-primary:hover { background: #0369a1; }
.btn-action { background: #059669; }
.btn-action:hover { background: #047857; }
.btn-purple { background: #7c3aed; }
.btn-purple:hover { background: #6d28d9; }
.mt-2 { margin-top: 8px; }
.mt-3 { margin-top: 14px; }
.hint { font-size: 12px; color: #64748b; margin-top: 4px; display: block; }
.text-success { color: #059669; }
.result-box {
  margin-top: 14px;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.6;
}
.result-box.success {
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  color: #065f46;
}
.result-box.error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}
.code-preview {
  margin-top: 8px;
  font-size: 11px;
}
.code-preview code {
  display: block;
  background: #1e293b;
  color: #38bdf8;
  padding: 6px 10px;
  border-radius: 6px;
  word-break: break-all;
  margin-top: 4px;
}
</style>
